import { getBusinesses } from "./api_calls/getBusinesses";
import { getCandleData } from "@/_core/data/api_calls/getCandleData";
import { getClients } from "./api_calls/getClients";
import { getEndProducts } from "./api_calls/getEndProducts";
import { getPlants } from "./api_calls/getPlant";
import { getProviders } from "./api_calls/getProvidert";
import { saveUserPreferences } from "@/_core/data/SaveToDB";

// Definition of available layouts
export const layouts = {
  1: [1, 1],
  2: [1, 2],
  3: [1, 3],
  4: [2, 1],
  5: [2, 2],
  6: [2, 3],
};

// Definition layouts dropdown
export const layoutOptions = [
  { label: String.fromCharCode(0x2610), value: "1x1", windows: 1 },
  { label: " | ", value: "1x2", windows: 2 },
  { label: " | | ", value: "1x3", windows: 3 },
  { label: String.fromCharCode(0x229f), value: "2x1", windows: 2 },
  { label: String.fromCharCode(0x251c), value: "1+2", windows: 3 },
  { label: String.fromCharCode(0x2524), value: "2+1", windows: 3 },
  { label: String.fromCharCode(0x229e), value: "2x2", windows: 4 },
  { label: String.fromCharCode(0x22a1), value: "2x3", windows: 6 },
];

// default overview_preferences
export function initOverviewPreferences(overview_preferences) {
  const defaults = {
    selectedLayout: {
      label: String.fromCharCode(0x2610),
      value: "1x1",
      windows: 1,
    },
    currentLayout: Array(6)
      .fill()
      .map(() => ({
        componentId: null,
        variables: { assetType: 1, selectedAsset: [2], showFields: false },
      })),
    color_theme: true,
    news_categories: ["commodity"],
  };
  let prefs = overview_preferences || {};
  let prefsChanged = false; // flag to detect if we modified anything
  // Validate selectedLayout
  if (
    !prefs.selectedLayout ||
    typeof prefs.selectedLayout.label !== "string" ||
    typeof prefs.selectedLayout.value !== "string"
  ) {
    prefs.selectedLayout = defaults.selectedLayout;
    prefsChanged = true;
  }
  // Validate currentLayout
  if (!Array.isArray(prefs.currentLayout) || prefs.currentLayout.length !== 6) {
    prefs.currentLayout = defaults.currentLayout;
    prefsChanged = true;
  } else {
    // === 3. Validar cada slot del layout usando defaultSingleComponents ===
    prefs.currentLayout = prefs.currentLayout.map((slot, i) => {
      const originalSlot = { ...slot };
      let hasChanged = false;

      // Si no hay componente, usar default genérico
      if (!slot || typeof slot.componentId !== "string") {
        hasChanged = true;
        return defaults.currentLayout[i];
      }

      // Obtener el componente por defecto real
      const defaultComponent = defaultSingleComponents(slot.componentId);

      // Si el componente no existe en defaults → fallback genérico
      if (!defaultComponent) {
        hasChanged = true;
        return defaults.currentLayout[i];
      }

      // Validar variables
      const defaultVars = defaultComponent.variables;
      const currentVars = slot.variables || {};

      const validatedVars = {};
      let varsChanged = false;

      // Iterar sobre las variables esperadas del componente
      for (const [key, defaultValue] of Object.entries(defaultVars)) {
        const currentValue = currentVars[key];

        // Si falta o tipo incorrecto → usar default
        if (
          currentValue === undefined ||
          typeof currentValue !== typeof defaultValue
        ) {
          validatedVars[key] = defaultValue;
          varsChanged = true;
        } else {
          validatedVars[key] = currentValue;
        }
      }

      // Si hay variables extra (no esperadas), eliminarlas
      for (const key of Object.keys(currentVars)) {
        if (!(key in defaultVars)) {
          varsChanged = true;
          // No incluir en validatedVars
        }
      }

      if (varsChanged) hasChanged = true;

      // Reconstruir slot
      const newSlot = {
        componentId: slot.componentId,
        variables: validatedVars,
      };

      if (hasChanged) prefsChanged = true;

      // Solo marcar cambio si realmente hubo modificación
      return hasChanged ? newSlot : originalSlot;
    });
  }
  // Validate color_theme
  if (prefs.color_theme === undefined) {
    prefs.color_theme = defaults.color_theme;
    prefsChanged = true;
  }

  // Validate news_categories
  if (!Array.isArray(prefs.news_categories)) {
    prefs.news_categories = defaults.news_categories;
    prefsChanged = true;
  }

  // Save cleaned object
  const cleanedPrefs = {
    selectedLayout: prefs.selectedLayout,
    currentLayout: prefs.currentLayout,
    color_theme: prefs.color_theme,
    news_categories: prefs.news_categories,
    news: prefs.news,
  };

  return {
    overview_preferences: cleanedPrefs,
    prefsChanged,
  };
}

// List of components the user can choose from
export const availableComponents = [
  { id: "newsWidget", label: "News", type: "news" },
  { id: "technicalChart", label: "Technical Analysis", type: "chart" },
  { id: "commFuturesChart", label: "Futures Chart", type: "chart" },
  { id: "commodityTable", label: "Futures Table", type: "table" },
  { id: "commoditySpread", label: "Spread Analysis", type: "table" },
  { id: "currencySpotTable", label: "Currency Spot Table", type: "table" },
  // { id: "currencyForward", label: "Currency Derivatives", type: "chart" },
  {
    id: "assetsTable",
    label: "Int Rates / Credit / Index / ETF Table",
    type: "table",
  },
];

// default layout
export const currentLayout = [
  {
    componentId: "technicalChart",
    variables: { assetType: 1, selectedAsset: 3 },
  },
  {
    componentId: "commodityTable",
    variables: { assetType: 1, selectedAsset: 3 },
  },
  {
    componentId: "technicalChart",
    variables: { assetType: 0, selectedAsset: 3 },
  },
  {
    componentId: "technicalChart",
    variables: { assetType: 2, selectedAsset: 4 },
  },
  {
    componentId: "technicalChart",
    variables: { assetType: 3, selectedAsset: 26 },
  },
  {
    componentId: "technicalChart",
    variables: { assetType: 4, selectedAsset: 21 },
  },
];

export const defaultMessage = {
  change: null,
  close: null,
  continuous_contract: null,
  contract: null,
  contract_month_year: null,
  high: null,
  low: null,
  open: null,
  openinterest: null,
  prev_close: null,
  prev_date: null,
  time: null,
};

// default variable selection for commodityTable
export function defaultSingleComponents(selectedComponent) {
  if (selectedComponent == "technicalChart") {
    return {
      componentId: selectedComponent,
      variables: {
        assetType: 1,
        irAvailableTerms: [{ label: "...", value: "..." }],
        selectedAsset: 3,
        selectedTerm: null,
        showFields: null,
      },
      data: {
        chartDataRaw: [],
        showLoading: true,
        spot: null,
        spotFactor: null,
        showLoadingSpot: true,
        spotAnimate: null,
        irSelectedTerm: null,
      },
    };
  } else if (selectedComponent == "commFuturesChart") {
    return {
      componentId: selectedComponent,
      variables: {
        assetType: 1,
        selectedAsset: 3,
        showFields: null,
      },
      data: {},
    };
  } else if (selectedComponent == "commodityTable") {
    return {
      componentId: selectedComponent,
      variables: {
        assetType: 1,
        selectedAsset: [2],
        showFields: false,
      },
      data: { commodityTable: [] },
    };
  } else if (selectedComponent == "commoditySpread") {
    return {
      componentId: selectedComponent,
      variables: {
        assetType: 1,
        selectedAsset: 10,
        selectedAsset2: 9,
        showFields: false,
      },
      data: { spreadTable: [] },
    };
  } else if (
    selectedComponent == "currencySpotTable" ||
    selectedComponent == "assetsTable"
  ) {
    return {
      componentId: selectedComponent,
      variables: { showFields: false },
      data: { spotTable: [] },
    };
  } else if (selectedComponent == "newsWidget") {
    return {
      componentId: selectedComponent,
      variables: {},
      data: { newsList: [], isLoading: false },
    };
  } else {
    return null;
  }
}

// Default layout (example: 6 slots with predefined components)
export const defaultLayout = [
  { componentId: null, variables: {}, data: {} },
  { componentId: null, variables: {}, data: {} },
  { componentId: null, variables: {}, data: {} },
  { componentId: null, variables: {}, data: {} },
  { componentId: null, variables: {}, data: {} },
  { componentId: null, variables: {}, data: {} },
];

// Default currency assets
export const defaultCurrencies = [
  {
    name: "Mayors",
    assets: [
      "AUDUSD",
      "CHFUSD",
      "EURGBP",
      "EURUSD",
      "GBPUSD",
      "NZDUSD",
      "USDCAD",
      "USDJPY",
      "USDNOK",
      "USDSEK",
    ],
  },
  {
    name: "Latin America",
    assets: [
      "USDARS",
      "USDBRL",
      "USDCLP",
      "USDCOP",
      "USDMXN",
      "USDPEN",
      "USDPYG",
    ],
  },
  {
    name: "Other",
    assets: [
      "USDAED",
      "USDCNY",
      "USDEGP",
      "USDGHS",
      "USDIDR",
      "USDILS",
      "USDINR",
      "USDMYR",
      "USDSGD",
    ],
  },
];

// Default currency assets
export const defaultSpotTable = [
  { name: "Mayors", data: [] },
  { name: "Latin America", data: [] },
  { name: "Other", data: [] },
];

// Default currency assets
export const defaultAssets = [
  // { name: "Interest Rates / Credit", assets: ["USD LIBOR", "IBR COL", "CDT COL", "CDS COL", "CDS BRA", "CDS MEX", "CDS CHL", "CDS DEU", "CDS FRA", "CDS ITA", "CDS ESP", "CDS GRC", "CDS CHN", "CDS GBR", "CDS JPN", "CDS USA", "OIS USA", "SOFR", "EFFR USA", "US TREASURIES", "TES 0 COUPON", "EMBI GLOBAL", "EMBI LATAM", "EMBI COL", "EMBI CHL", "EMBI BRA", "EMBI MEX", "EMBI ARG", "EMBI PER"] },
  {
    name: "Interest Rates / Credit",
    assets: [
      "CDS COL",
      "CDS BRA",
      "CDS MEX",
      "CDS CHL",
      "CDS DEU",
      "CDS FRA",
      "CDS ITA",
      "CDS ESP",
      "CDS GRC",
      "CDS CHN",
      "CDS GBR",
      "CDS JPN",
      "CDS USA",
      "OIS USA",
      "SOFR",
      "EFFR USA",
      "US TREASURIES",
      "TES 0 COUPON",
      "EMBI GLOBAL",
      "EMBI LATAM",
      "EMBI COL",
      "EMBI CHL",
      "EMBI BRA",
      "EMBI MEX",
      "EMBI ARG",
      "EMBI PER",
    ],
  },
  {
    name: "Indices",
    assets: [
      "BANK",
      "COLCAP",
      "DJI",
      "DXY",
      "GSCI",
      "IBOVESPA BRA",
      "IPC MEX",
      "IPSA CHL",
      "LATFX",
      "NASDAQ",
      "S&P500",
      "VIX",
    ],
  },
  {
    name: "ETFs",
    assets: [
      "BRA ETF",
      "CHL ETF",
      "COL ETF",
      "EMB ETF",
      "EM ETF",
      "FIN ETF",
      "GSCI ETF",
      "LCAP ETF",
      "LCAP EX US ETF",
      "MX ETF",
    ],
  },
  {
    name: "Futures",
    assets: [
      "T-BOND FUT",
      "10Y T-NOTE FUT",
      "5Y T-NOTE FUT",
      "2Y T-NOTE FUT",
      "S&P-GSCI FUT",
    ],
  },
];

// Default asset (idx, int rates, etc, futures) table
export const defaultAssetTable = [
  { name: "Interest Rates / Credit", data: [] },
  { name: "Indices", data: [] },
  { name: "ETFs", data: [] },
  { name: "Futures", data: [] },
];

// Default spread table
export const defaultSpreadTable = [
  { name: "Commodity 1", data: [] },
  { name: "Commodity 2", data: [] },
  { name: "Switch", data: [] },
  {
    name: ["Curr1", "Curr2", "unit1", "unit2", "rate"],
    data: { conversion: null, rate: null },
  },
];

// Default row for commodities
export const defaultBlankRow = (month, nextClose = null) => ({
  close: nextClose === undefined ? null : nextClose,
  continuous_contract: null,
  contract_month_year: month,
  high: null,
  last_trading_date: null,
  low: null,
  observation_date: null,
  open: null,
  openinterest: null,
  prev_close: null,
  volumes: null,
  isblank: true,
});

// ========================== Old ========================= //

/**
 * Function used in Overview.vue to retrieve historical data.
 * @param {object} _this - Component context (typically 'this')
 * @param {number} chartNum - Chart identifier (1 to 4)
 * @param {number} selAssetType - Asset type: 0 = currency, 1 = commodity, 2 = index
 * @param {number} assetNumber - Primary key (ID) of the selected asset
 * @param {number} [commodityContract=1] - Contract month for commodity continuation (optional)
 */
export function getChartData(
  _this,
  chartNum,
  selAssetType,
  assetNumber,
  commodityContract = 1,
) {
  if (chartNum === 1) {
    _this.chartDataRaw1 = [];
    _this.spot1 = null;
    _this.showLoadingAsset1 = true;
  } else if (chartNum === 2) {
    _this.chartDataRaw2 = [];
    _this.spot2 = null;
    _this.showLoadingAsset2 = true;
  } else if (chartNum === 3) {
    _this.chartDataRaw3 = [];
    _this.spot3 = null;
    _this.showLoadingAsset3 = true;
  } else if (chartNum === 4) {
    _this.chartDataRaw4 = [];
    _this.spot4 = null;
    _this.showLoadingAsset4 = true;
  }
  if (selAssetType === 0) {
    // 0: CURRENCY
    _this.currencyNumber = assetNumber;
    return getCandleData(_this, "technical_analysis_historical", {
      currencyNumber: assetNumber,
    });
  } else if (selAssetType === 1) {
    // 1: COMMODITY
    return getCandleData(_this, "commodity", {
      commodityNumber: assetNumber,
      contract: commodityContract,
    });
  } else if (selAssetType === 2) {
    // 3: INDEX
    return getCandleData(_this, "index", {
      index: assetNumber,
      initDate: _this.initCurrencyDate,
    });
  }
}

/**
 * Converts an asset type string to its corresponding index.
 * @param {string} asset - Asset type as a string ("currency", "commodity", or "index")
 * @returns {number} - Corresponding index: 0 = currency, 1 = commodity, 2 = index; returns -1 if unrecognized
 */
export function assetType(asset) {
  switch (asset) {
    case "currency":
      return 0;
    case "commodity":
      return 1;
    case "index":
      return 2;
    default:
      return -1;
  }
}

/**
 * Generates table headers by extracting keys from the first row of data,
 * excluding predefined ones.
 * @param {object} _this - Component context containing table data
 */
export function createTableHeaders(_this) {
  if (_this.tableData.length === 0) {
    _this.tableHeaders = ["spot"];
  } else {
    let keys = Object.keys(_this.tableData[0]);
    _this.tableHeaders = keys.filter(
      (d) =>
        d !== "row" &&
        d !== "contract" &&
        d !== "close" &&
        d !== "asset" &&
        d !== "change" &&
        d !== "show" &&
        d !== "previous_value" &&
        d !== "render" &&
        d !== "type",
    );
  }
}

/**
 * If the client doesn't have a view, this is the default
 */
export function defaultViewSelection() {
  return [
    "EURUSD",
    "GBPUSD",
    "USDBRL",
    "USDCOP",
    "USDMXN",
    "USDMYR",
    "BRENT",
    "CORN",
    "CPO CIF ROTT",
    "CPO MAL MYR",
    "CPO MAL USD",
    "PKO",
    "SOYBEAN",
    "SOYBEAN OIL",
    "SUGAR",
    "WTI",
    "DXY",
    "NASDAQ",
    "S&P500",
    "VIX",
  ];
}
/**
 * If the client doesn't have a view, this are the default preferences
 */
export function getDefaultOverviewPreferences(_this) {
  const items = _this.$store.state.user_preferences.all?.items;
  const overview_choices = items?.overview_choices;
  if (!overview_choices || Object.keys(overview_choices).length === 0) {
    let choices = {
      color_theme: false,
      selected_values: [
        "EURUSD",
        "GBPUSD",
        "USDBRL",
        "USDCOP",
        "USDMXN",
        "USDMYR",
        "BRENT",
        "CORN",
        "CPO CIF ROTT",
        "CPO MAL MYR",
        "CPO MAL USD",
        "PKO",
        "SOYBEAN",
        "SOYBEAN OIL",
        "SUGAR",
        "WTI",
        "DXY",
        "NASDAQ",
        "S&P500",
        "VIX",
      ],
    };
    if (items) {
      items.overview_choices = choices;
      saveUserPreferences(_this);
    }
    return { choices };
  } else {
    return { choices: overview_choices };
  }
}

/**
 * Sets default hedge preferences if none are defined by the client.
 * @param {object} _this - Component context (typically 'this')
 * @returns {object} choices - The hedge preference settings
 */
export function getDefaultHedgePreferences(_this) {
  const items = _this.$store.state.user_preferences.all?.items;
  const hedgePrefs = items?.hedges_choices;

  if (!hedgePrefs || Object.keys(hedgePrefs).length === 0) {
    const choices = { color_theme: true };
    if (items) {
      items.hedges_choices = choices;
      saveUserPreferences(_this);
    }
    return { choices };
  } else {
    return { choices: hedgePrefs };
  }
}

/**
 * get companyParameters
 * @param { object } companies initial company information
 * @param { function } [onEndProducts] optional callback invoked as soon as the
 *   end products resolve, receiving the (partially populated) companyParameters
 *   object.
 */
export async function getCompanyParameters(companies, onEndProducts) {
  let companyParameters = {
    businesses: [],
    clients: [],
    endProducts: [],
    plants: [],
    providers: [],
    ready: false,
  };
  await getEndProducts(companies)
    .then((data) => {
      Object.assign(companyParameters, {
        endProducts: JSON.parse(JSON.stringify(data)),
      });
    })
    .catch(function(error) {
      console.error(error);
    });
  if (typeof onEndProducts === "function") {
    onEndProducts(companyParameters);
  }
  await Promise.all([
    getPlants(companies)
      .then((data) => {
        Object.assign(companyParameters, {
          plants: JSON.parse(JSON.stringify(data)),
        });
      })
      .catch(function(error) {
        console.error(error);
      }),
    getProviders(companies)
      .then((data) => {
        Object.assign(companyParameters, {
          providers: JSON.parse(JSON.stringify(data)),
        });
      })
      .catch(function(error) {
        console.error(error);
      }),
    getClients(companies)
      .then((data) => {
        Object.assign(companyParameters, {
          clients: JSON.parse(JSON.stringify(data)),
        });
      })
      .catch(function(error) {
        console.error(error);
      }),
    getBusinesses(companies)
      .then((data) => {
        Object.assign(companyParameters, {
          businesses: JSON.parse(JSON.stringify(data)),
        });
      })
      .catch(function(error) {
        console.error(error);
      }),
  ]);
  Object.assign(companyParameters, { ready: true });
  return companyParameters;
}
